import dayjs from "dayjs";
import { useMemo, useState } from "react";

import {
  IFitbitSleepResponse,
  IFitbitSleepSession,
} from "../../../../../../@types/Fitbit";
import { useFitbitSleep } from "../../../../../../hooks/queries/fitbit";
import useIsMobile from "../../../../../../hooks/useIsMobile";
import { useUserPreferences } from "../../../../../../hooks/useUserPreferences";
import { userService } from "../../../../../../services/userService";
import { getShortFormattedDate } from "../../../../../../utils/dateFormatter";
import {
  HeaderContainer,
  MeasurementContainer,
  Subtitle,
  Title,
} from "../../MeasurementModal.styles";
import {
  DATE_FILTER_ALL,
  DATE_FILTER_CUSTOM,
  DateFilterOptionType,
} from "../CustomOptionsBar";
import { FitbitOptionsBar } from "../FitbitSection/FitbitOptionsBar";
import {
  FitbitSleepChart,
  FitbitSleepChartRow,
} from "../FitbitSleepChart/FitbitSleepChart";
import { FitbitSleepSelectedSession } from "../FitbitSleepSelectedSession/FitbitSleepSelectedSession";
import { FitbitSleepTable } from "../FitbitSleepTable/FitbitSleepTable";
import { SubtitleConatiner } from "../MeasurementSection/MeasurementSection.styles";
import { ModalSkeleton } from "../ModalSkeleton";

type FitbitSleepSectionProps = {
  patientId?: string;
  cardTitle: string;
  isPinnedGraph?: boolean;
  onSelectedTableView?: (showTable: boolean) => void;
  selectedSession?: IFitbitSleepSession;
  onSessionChange?: (session?: IFitbitSleepSession) => void;
};

function dateKey(value: string): string {
  return value.split("T")[0];
}

/**
 * For a given day, pick the main-sleep session that matches its minutesAsleep
 * (this is how the API picks a winner when multiple providers report the same night).
 * Falls back to the first main-sleep session for that date.
 */
function resolveMainSession(
  response: IFitbitSleepResponse | undefined,
  day: IFitbitSleepResponse["days"][number]
): IFitbitSleepSession | undefined {
  if (!response) return undefined;
  const dayKey = dateKey(day.date);
  const candidates = response.sessions.filter(
    (session) => session.isMainSleep && dateKey(session.dateOfSleep) === dayKey
  );
  if (candidates.length === 0) return undefined;
  return (
    candidates.find((session) => session.minutesAsleep === day.minutesAsleep) ??
    candidates[0]
  );
}

export function FitbitSleepSection({
  patientId,
  cardTitle,
  isPinnedGraph,
  onSelectedTableView,
  selectedSession,
  onSessionChange,
}: FitbitSleepSectionProps) {
  const { getDateRangeFilter, portalPreferences, changePortalPreferences } =
    useUserPreferences();
  const { widthView } = useIsMobile();

  // Same breakpoints as SpirometrySection — sleep also opens a side panel,
  // so the chart needs to leave room for it on laptop-sized viewports.
  const chartWidth = useMemo(() => {
    if (widthView > 1530) return 834;
    if (widthView < 1250) return 550;
    return 664;
  }, [widthView]);

  const dateRangeFilter = getDateRangeFilter();
  const initialFilter: DateFilterOptionType = {
    endDate: dateRangeFilter.endDate,
    id: dateRangeFilter.id,
    startDate: dateRangeFilter.startDate,
  };

  const [selectedFilterDate, setSelectedFilterDate] =
    useState<DateFilterOptionType>(initialFilter);
  const [showTable, setShowTable] = useState(false);
  const selectedSessionId = selectedSession?.id;

  const { sleepData, isSleepFetching, isSleepRefetching } = useFitbitSleep(
    parseInt(patientId ?? "0", 10),
    selectedFilterDate.startDate.startOf("day"),
    selectedFilterDate.endDate.endOf("day"),
    !!patientId && patientId !== "0"
  );

  const chartRows: FitbitSleepChartRow[] = useMemo(() => {
    if (!sleepData) return [];
    return sleepData.days.map((day) => {
      const [yearStr, monthStr, dayStr] = dateKey(day.date).split("-");
      const date = new Date(
        parseInt(yearStr, 10),
        parseInt(monthStr, 10) - 1,
        parseInt(dayStr, 10),
        12,
        0,
        0,
        0
      );
      const session = resolveMainSession(sleepData, day);
      return {
        timeValue: date.getTime(),
        hoursAsleep: day.hoursAsleep,
        minutesAsleep: day.minutesAsleep,
        sessionId: session?.id,
        provider: session?.provider,
        stageSummary: session?.stageSummary ?? null,
      };
    });
  }, [sleepData]);

  const sessionsById = useMemo(() => {
    const map = new Map<string, IFitbitSleepSession>();
    sleepData?.sessions.forEach((session) => {
      map.set(session.id, session);
    });
    return map;
  }, [sleepData]);

  const allRangeFromResponse = useMemo(() => {
    if (!sleepData?.dataStartDate || !sleepData?.dataEndDate) return null;
    return {
      startDate: dayjs(sleepData.dataStartDate),
      endDate: dayjs(sleepData.dataEndDate),
    };
  }, [sleepData?.dataStartDate, sleepData?.dataEndDate]);

  const displayDateRange = useMemo(() => {
    if (selectedFilterDate.id === DATE_FILTER_ALL && allRangeFromResponse) {
      return allRangeFromResponse;
    }
    return selectedFilterDate;
  }, [selectedFilterDate, allRangeFromResponse]);

  const formattedDateRange = `${getShortFormattedDate(
    displayDateRange.startDate.format()
  )} - ${getShortFormattedDate(displayDateRange.endDate.format())}`;

  const handleFilterChange = (newFilter: DateFilterOptionType) => {
    const { id } = newFilter;
    if (id === DATE_FILTER_CUSTOM || id !== selectedFilterDate.id) {
      setSelectedFilterDate(newFilter);
      onSessionChange?.(undefined);

      const newPortalPreferences = {
        ...portalPreferences,
        dateRangeFilter: id,
        customFromDate:
          id === DATE_FILTER_CUSTOM ? newFilter.startDate : undefined,
        customToDate: id === DATE_FILTER_CUSTOM ? newFilter.endDate : undefined,
      };
      changePortalPreferences(newPortalPreferences);

      if (id !== DATE_FILTER_CUSTOM) {
        userService.updateDateRangeFilter(id);
      }
    }
  };

  const handleTableViewChange = (enableTableView: boolean) => {
    setShowTable(enableTableView);
    onSelectedTableView?.(enableTableView);
    if (enableTableView) {
      onSessionChange?.(undefined);
    }
  };

  const handleSelectSession = (row: FitbitSleepChartRow) => {
    if (!row.sessionId) return;
    onSessionChange?.(sessionsById.get(row.sessionId));
  };

  const providerLabel = useMemo(() => {
    const selected = selectedSessionId
      ? sessionsById.get(selectedSessionId)
      : undefined;
    const provider =
      selected?.provider ??
      sleepData?.sessions.find((s) => s.isMainSleep)?.provider;
    if (provider === "GoogleHealth") return "Google Health";
    return provider ?? null;
  }, [selectedSessionId, sessionsById, sleepData]);

  const isLoading = isSleepFetching || isSleepRefetching;
  const isEmpty = !sleepData || chartRows.length === 0;

  if (isLoading) {
    return <ModalSkeleton isPinnedGraph={isPinnedGraph} width={chartWidth} />;
  }

  return (
    <MeasurementContainer>
      <HeaderContainer style={{ marginBottom: showTable ? "0px" : "20px" }}>
        <Title>{cardTitle}</Title>
        <Subtitle>{formattedDateRange}</Subtitle>

        <SubtitleConatiner>
          <FitbitOptionsBar
            onChange={handleFilterChange}
            onTableViewChange={handleTableViewChange}
            selectedFilterDate={selectedFilterDate}
            showTable={showTable}
            isEmpty={isEmpty}
          />
        </SubtitleConatiner>
      </HeaderContainer>

      {showTable ? (
        <FitbitSleepTable
          data={chartRows}
          isPinnedGraph={isPinnedGraph}
          onRowClick={handleSelectSession}
        />
      ) : (
        <FitbitSleepChart
          data={chartRows}
          dateInterval={selectedFilterDate}
          allRange={allRangeFromResponse}
          statistics={sleepData?.statistics}
          height="78%"
          width={isPinnedGraph ? "100%" : chartWidth}
          isPinnedGraph={isPinnedGraph}
          hasSidePanel={isPinnedGraph && !!selectedSession}
          isEmpty={isEmpty}
          providerLabel={providerLabel}
          selectedSessionId={selectedSessionId}
          onSelectSession={handleSelectSession}
        />
      )}
    </MeasurementContainer>
  );
}

export { FitbitSleepSelectedSession };
